import { BaseService } from "core/services";
import { FacilityOrderTemplateRouteService } from "application/facility-order/services";

class FacilityOrderTemplateService extends BaseService {
	constructor(apiClient) {
		super(apiClient, new FacilityOrderTemplateRouteService());
	}

	put(url, resource) {
		const connectionId = sessionStorage.getItem("connectionId");
		return this.apiClient.put(url, resource, connectionId ? { connectionId: connectionId } : null);
	}

	post(url, resource) {
		const connectionId = sessionStorage.getItem("connectionId");
		return this.apiClient.post(url, resource, connectionId ? { connectionId: connectionId } : null);
	}

	update(resource) {
		const url = this.routeService.update(resource);
		return this.put(url, resource);
	}

	batchUpdate(resources) {
		const url = this.routeService.batchUpdate(resources);
		return this.put(url, resources)
	}

	validatePatientHeightAndWeight(facilityOrderId, patientId) {
		const url = this.routeService.validatePatientHeightAndWeight({ facilityOrderId, patientId });
		return this.apiClient.get(url);
	}

	getMedicalNecessityFormReportUrl(id) {
		const url = this.routeService.getMedicalNecessityFormReportUrl(id);
		return this.apiClient.appOptions.apiUrl + url;
	}

	isReviewedFacilityOrderAsync(facilityOrderId) {
		const url = this.routeService.isReviewedFacilityOrderAsync(facilityOrderId);
		return this.apiClient.get(url);
	}

	isNewTemplate(facilityOrderId, patientId) {
		const url = this.routeService.isNewTemplate({ facilityOrderId, patientId });
		return this.apiClient.get(url);
	}

	getStandardWrittenOrderReportUrl(id) {
		const url = this.routeService.getStandardWrittenOrderReportUrl(id);
		return this.apiClient.appOptions.apiUrl + url;
	}

	addTrackingCase(facilityOrderTemplateId) {
		const url = this.routeService.addTrackingCase(facilityOrderTemplateId);
		return this.post(url);
	}

	createBySubCategory(subCategoryId, facilityOrderId, patientId, eligibilityStatusId) {
		const url = this.routeService.createBySubCategory();
		return this.post(url, { subCategoryId, facilityOrderId, patientId, eligibilityStatusId });
	}

	createTemplateAndItemsNewFlow(resource) {
		const url = this.routeService.createTemplateAndItemsNewFlow();
		return this.post(url, resource);
	}

	createMasterPatientTemplateBySubCategory(subCategoryId, facilityOrderId) {
		const url = this.routeService.createMasterPatientTemplateBySubCategory();
		return this.post(url, { subCategoryId, facilityOrderId });
	}

	importFacilityOrderTemplate(resource) {
		const url = this.routeService.importFacilityOrderTemplate(resource);
		return this.put(url, resource);
	}

	reloadFacilityOrderTemplate(resource) {
		const url = this.routeService.reloadFacilityOrderTemplate(resource);
		return this.put(url, resource);
	}

	validateFacilityOrderTemplate(resource) {
		const url = this.routeService.validateFacilityOrderTemplate(resource);
		return this.put(url, resource);
	}

	getFacilityOrderTemplateValidation(id, options) {
		const url = this.routeService.getFacilityOrderTemplateValidation(id, options);
		return this.apiClient.get(url);
	}

	updateInactiveReason(resource) {
		const url = this.routeService.updateInactiveReason(resource);
		return this.put(url, resource);
	}

	updatePortalNote(resource) {
		const url = this.routeService.updatePortalNote(resource);
		return this.put(url, resource);
	}

	updatePriorAuthNeeded(resource) {
		const url = this.routeService.updatePriorAuthNeeded(resource);
		return this.put(url, resource);
	}

	activateFacilityOrderTemplate(resource) {
		const url = this.routeService.activateFacilityOrderTemplate(resource);
		return this.put(url, resource);
	}

	updateTemplateAndItems(resource) {
		const url = this.routeService.updateTemplateAndItems(resource);
		return this.put(url, resource);
	}

	updateNewFlowClinicalTemplate(resource) {
		const url = this.routeService.updateNewFlowClinicalTemplate(resource);
		return this.put(url, resource);
	}

	updateNewFlowUSIntakeTemplate(resource) {
		const connectionId = sessionStorage.getItem("connectionId");
		const url = this.routeService.updateNewFlowUSIntakeTemplate(resource);
		return this.put(url, resource, connectionId ? { connectionId: connectionId } : null);
	}

	getTemplateNavigation(facilityOrderId, patientId, itemCategoryIds, inActive, nonCurrent) {
		const url = this.routeService.getTemplateNavigation({ facilityOrderId, patientId, itemCategoryIds, inActive, nonCurrent });
		return this.apiClient.get(url);
	}

	getOldDownloadUrl(filter) {
		return this.apiClient.appOptions.apiUrl + this.routeService.oldDownload(filter);
	}

	getDownloadUrl(filter) {
		return this.apiClient.appOptions.apiUrl + this.routeService.download(filter);
	}

	getDownloadNewFlowUrl(filter) {
		return this.apiClient.appOptions.apiUrl + this.routeService.downloadNewFlow(filter);
	}

	getDownloadNewFlowCsvUrl(filter) {
		return this.apiClient.appOptions.apiUrl + this.routeService.downloadNewFlowCsv(filter);
	}

	deleteNewTemplate(id) {
		const url = this.routeService.deleteNewTemplate(id);
		const connectionId = sessionStorage.getItem("connectionId");
		return this.apiClient.delete(url, connectionId ? { connectionId: connectionId } : null);
	}

	validateItems(id, options = {}) {
		const url = this.routeService.validateItems(id, options);
		return this.apiClient.get(url);
	}

	reloadTemplates(resource) {
		const url = this.routeService.reloadTemplates(resource);
		return this.put(url, resource);
	}

	isTemplateCategoryLinkedWithDoNotSupplyAreaOfCareOnFacility(resource) {
		const url = this.routeService.isTemplateCategoryLinkedWithDoNotSupplyAreaOfCareOnFacility(resource);
		return this.apiClient.get(url);
	}

	setDoNotSupply(resource) {
		const url = this.routeService.setDoNotSupply(resource);
		return this.put(url, resource);
	}

	updateMissingProductNote(resource) {
		let url = this.routeService.updateMissingProductNote(resource);
		if (resource.missingProductNote === "" || !resource.missingProductNote) {
			url += `${""}`;
		}
		return this.put(url, resource);
	}

	updateMissingProduct(resource) {
		const url = this.routeService.updateMissingProduct(resource);
		return this.put(url, resource);
	}

	resolveMissingProductNote(resource) {
		const url = this.routeService.resolveMissingProductNote(resource);
		return this.put(url, resource);
	}

	changeTemplateStatus(resource) {
		const url = this.routeService.changeTemplateStatus(resource);
		return this.put(url, resource);
	}

	createMasterPatientTemplateBySubCategoryNewFlow(facilityOrderTemplateItems, facilityOrderId, subCategoryId, clinicalStatusId, secondaryInsuranceVerified) {
		const url = this.routeService.createMasterPatientTemplateBySubCategoryNewFlow();
		return this.post(url, { facilityOrderTemplateItems, facilityOrderId, subCategoryId, clinicalStatusId, secondaryInsuranceVerified });
	}

	updateFacilityOrderTemplateIntakeStatuses(resource) {
		const url = this.routeService.updateFacilityOrderTemplateIntakeStatuses(resource);
		return this.put(url, resource);
	}

	findReplacementItems(filter) {
		const url = this.routeService.findReplacementItems(filter);
		return this.apiClient.get(url);
	}

	isReviewedFacilityOrderNewFlowAsync(facilityOrderId) {
		const url = this.routeService.isReviewedFacilityOrderNewFlowAsync(facilityOrderId);
		return this.apiClient.get(url);
	}

	isIVPoleItemNeeded(resource) {
		const url = this.routeService.isIVPoleItemNeeded(resource);
		return this.apiClient.get(url);
	}

	updateInvoiceNote(facilityOrderTemplateId, note) {
		const url = this.routeService.updateInvoiceNote(facilityOrderTemplateId, note);
		return this.apiClient.put(url);
	}

	updateCustomOrderingDoctor(resource) {
		const url = this.routeService.updateCustomOrderingDoctor(resource);
		return this.put(url, resource);
	}

	createNotCurrentTubeExtraSupplyTemplate(resources) {
		const connectionId = sessionStorage.getItem("connectionId");
		const url = this.routeService.createNotCurrentTubeExtraSupplyTemplate(resources);
		return this.apiClient.post(url, resources, connectionId ? { connectionId: connectionId } : null)
	}

	importCheck(id, actionName) {
		const url = this.routeService.importCheck(id, actionName);
		return this.apiClient.get(url);
	}

	hasBackorderedItem(facilityOrderId) {
		const url = this.routeService.hasBackorderedItem(facilityOrderId);
		return this.apiClient.get(url);
	}

	updateClinicalStatusNote(resource) {
		const url = this.routeService.updateClinicalStatusNote(resource);
		return this.apiClient.put(url, resource);
	}
}

export default FacilityOrderTemplateService;